import * as ko from "knockout";
import PromiseRequest from "../../req/PromiseRequest";
import {DEFAULT_SEARCH_PAGE, ObjectTypeId, REQUEST_THROTTLE, SPINNER} from "../../constants";
import {
    IMessageData,
    IObjectData,
    IResponse,
    IRouteDefinitionParams,
    ISearchObjectParams,
    ISyncField,
    ITableColumn,
    TSyncfield
} from "../../index";
import {_} from 'lodash';
import {global, lm} from "../../appContext";
import {storeObservable} from "../../util/sessionstore";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {
    createErrorMessage,
    getSearchMarkup,
    getSourceTranslation,
    getSyncField,
    getSyncFieldValue,
    getWhiteWashedSearchKey
} from "../../util/misc";
import {getSearchObjectParams} from "../../util/request";
import {TServiceName} from "../../constants/ApiServices";
import {nsRoute} from "../../util/route";
import {nsSearch, PLEASE_WAIT_MESSAGE} from "../../constants/Language";
import {nsSyncFields} from "../../req/nsSyncFields";
import {IRequestObject} from "../../req/index";
import {getSearchBoxHeadingKey} from "../search/boxHeadingKey";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import search_basicHtml from './search.basic.html?raw';

export let template: string = search_basicHtml;

export abstract class viewModel extends ParsedRouteDataDecorator {
    data: KnockoutObservableArray<IObjectData> = ko.observableArray<IObjectData>([]);
    isRequesting: KnockoutObservable<boolean> = ko.observable(false);
    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    objecttypeid!: ObjectTypeId;
    searchascending: KnockoutObservable<boolean> = ko.observable(true);
    searchcolumn: KnockoutObservable<string> = ko.observable("");
    searchkey: KnockoutObservable<string> = ko.observable("");
    searchpagenr: KnockoutObservable<number> = ko.observable(DEFAULT_SEARCH_PAGE);
    searchpages: KnockoutObservable<number | null> = ko.observable(null);
    searchtotalresults: KnockoutObservable<number | null> = ko.observable(null);
    showAll: KnockoutObservable<boolean> = ko.observable(false);
    abstract table: ITableColumn[];

    protected kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private timeoutId!: number;
    protected serviceName: TServiceName = 'searchobject';
    protected reqSearch: PromiseRequest = global.getPromiseRequest(this.serviceName);
    private numsearchresults: KnockoutObservable<number> = storeObservable("num.search.results");
    private syncfields: KnockoutObservable<ISyncField[] | null> = ko.observable(null);
    private req_syncfields!: IRequestObject
    protected readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null)

    constructor(params: IRouteDefinitionParams, autoRequest: boolean = true) {
        super(params);
        this.kosub.subscribe(this.showAll, () => this.preRequest());
        this.kosub.subscribe(this.searchascending, () => this.preRequest());
        this.kosub.subscribe(this.searchcolumn, () => this.preRequest());
        this.kosub.subscribe(this.searchkey, () => this.preRequest());
        this.kosub.subscribe(this.searchpagenr, () => {
            this.reqSearch.abort()
            clearTimeout(this.timeoutId);
            this.timeoutId = setTimeout(() => this.request(), REQUEST_THROTTLE);
        });

        this.kosub.subscribe(storeObservable("num.search.results"), () => this.preRequest());
        this.kosub.subscribe(storeObservable('language'), () => this.init())

        if (autoRequest) {
            requestAnimationFrame(() => this.init().then(() => this.preRequest()))
        }
    }

    protected init(): Promise<any> {
        this.isReady(false)
        this.messageData({message: PLEASE_WAIT_MESSAGE})
        return this.initSyncfields().then(() => {
            this.messageData(null)
            this.isReady(true)
        })
    }

    protected translate(key: TSyncfield): string {
        const sf = getSyncField(key, this.syncfields() ?? []);
        if (sf) {
            return getSourceTranslation(sf, global.sourceTranslation())
        }
        return lm.t(key) ?? ''
    }

    protected initSyncfields(): Promise<any> {
        this.req_syncfields?.promise.abort()
        this.req_syncfields = nsSyncFields.getSyncFields(this.__objectTypeId!)
        this.req_syncfields.request
            .then((data: ISyncField[]) => this.syncfields(data.length ? data : null))
            .catch(() => {
                // no syncfields, no problem - just fail silently
            })
        return this.req_syncfields.request
    }

    protected clearScheduledSearchRequest(): void {
        clearTimeout(this.timeoutId);
    }

    protected preRequest() {
        this.clearScheduledSearchRequest();
        if (!this.isReady()) {
            return;
        }
        this.reqSearch.abort();
        const a = this.showAll();
        const b = !this.showAll() && !!this.searchkey();
        this.searchtotalresults(null);
        if (a || b) {
            this.searchpagenr(DEFAULT_SEARCH_PAGE);
            this.searchpages(null);
            this.timeoutId = setTimeout(() => this.request(), 100);
        } else {
            this.data([]);
        }
    }

    protected request() {
        this.reqSearch.abort();
        const cache = global.cacheManager.getCache("searchobject", this.getParams(), true);
        if (cache) {
            this.onSuccess(cache.data);
            return;
        }
        this.isRequesting(true);
        this.reqSearch
            .request(this.getParams(), true)
            .then(this.onSuccess.bind(this))
            .catch((response: IResponse | any) => {
                this.isRequesting(false);
                const reason = response.lasterror || response.message || String(response);
                this.messageData(createErrorMessage(String(reason)))
            })
        ;
    }

    protected getParams(params: Partial<ISearchObjectParams> = {}): ISearchObjectParams {
        return getSearchObjectParams(params, {
            asc: this.searchascending(),
            objecttypeid: this.objecttypeid,
            pagenr: this.searchpagenr(),
            resultsprpage: this.numsearchresults(),
            searchkey: this.getRealSearchKey(),
            sortcol: this.searchcolumn(),
        });
    }

    protected onSuccess(response: IResponse) {
        const data = response.data;
        if (data.length) {
            this.messageData(null);
            this.searchascending(response.sortascending);
            this.searchcolumn(response.sortedby);
            this.searchpagenr(response.pagenr);
            this.searchpages(response.pages);
            this.searchtotalresults(response.totalresults);
            this.data(data);
        } else {
            this.messageData({message: nsSearch.MessageNoResult})
            this.searchpagenr(DEFAULT_SEARCH_PAGE);
            this.searchpages(null);
            this.searchtotalresults(null);
            this.data([]);
        }
        this.isRequesting(false);
    }

    getRealSearchKey() {
        return this.showAll() ? "" : this.searchkey();
    }

    onPreviousPageClick() {
        this.searchpagenr(this.searchpagenr() - 1);
        this.request();
    }

    onNextPageClick() {
        this.searchpagenr(this.searchpagenr() + 1);
        this.request();
    }

    isPreviousPageEnabled(): boolean {
        return this.searchpagenr() > 0;
    }

    isNextPageEnabled(): boolean {
        return this.searchpagenr() + 1 < (this.searchpages() ?? 0);
    }

    getPagingText = ko.pureComputed(() => {
        let isUnknown = this.searchpages() === null;
        let spinner = SPINNER;
        let current = isUnknown ? spinner : this.searchpagenr() + 1;
        let total = isUnknown ? spinner : Math.ceil((this.searchtotalresults() ?? 0) / storeObservable("num.search.results")());
        return `Side ${current} av ${total}`;
    });

    getSyncFieldValue(key: TSyncfield, obj: IObjectData) {
        let ret = getSyncFieldValue(key, obj.syncfields);
        if (this.showAll()) {
            return ret;
        }
        const isSearchable = !!this.table.find((item: ITableColumn) => item.sort);

        if (this.objecttypeid == ObjectTypeId.User) {
            const searchKey = this.getRealSearchKey();
            if (isSearchable && searchKey) {
                switch (key) {
                    case 'GivenName':
                    case 'Surname':
                        const isGn = this.table.find((item: ITableColumn) => item.key == 'GivenName');
                        const isSn = this.table.find((item: ITableColumn) => item.key == 'Surname');

                        if (isGn && isSn) {
                            const dn: string = getSyncFieldValue('DisplayName', obj.syncfields);
                            const gn: string = getSyncFieldValue('GivenName', obj.syncfields);
                            const sn: string = getSyncFieldValue('Surname', obj.syncfields);

                            const regExp = new RegExp(getWhiteWashedSearchKey(this.searchkey()), 'gi')
                            let match: RegExpExecArray | null
                            const indexes: number[] = []
                            while ((match = regExp.exec(dn)) != null) {
                                indexes.push(match.index)
                            }
                            const indexMarkStart = indexes.find(index => {
                                const withinGn = index <= gn.length;
                                const beyondGn = index + this.searchkey().length > gn.length;
                                return withinGn && beyondGn
                            })
                            if (indexMarkStart !== undefined) {
                                switch (key) {
                                    case 'GivenName': {
                                        if (indexMarkStart >= gn.length) {
                                            break;
                                        }
                                        return getSearchMarkup(gn.slice(0, indexMarkStart), this.searchkey()) + '<mark>' + gn.slice(indexMarkStart) + '</mark>'
                                    }
                                    case 'Surname': {
                                        const space = 1;
                                        const gnMarkLength = gn.length - indexMarkStart
                                        const snMarkLength = this.searchkey().length - (gnMarkLength + space)
                                        if (snMarkLength > 0) {
                                            return '<mark>' + sn.slice(0, snMarkLength) + '</mark>' + getSearchMarkup(sn.slice(snMarkLength), this.searchkey())
                                        }
                                    }
                                }
                            }
                        }
                }
            }
        }

        if (isSearchable && this.searchkey()) {
            if (_.isString(ret)) {
                let regex = new RegExp(this.searchkey().replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
                ret = ret.replace(regex, (match) => {
                    return "<mark>" + match + "</mark>";
                });
            }
        }

        return ret;
    }

    protected searchMessage = ko.pureComputed((): IMessageData | null => {
        if (this.data().length) {
            return null;
        }
        if (this.hasSearchKey() || this.isRequesting()) {
            return this.messageData() || {message: nsSearch.MessageLoading};
        }
        return {message: nsSearch.MessageInitial};
    });

    private hasSearchKey(): boolean {
        return this.showAll() || !!this.searchkey();
    }

    /** White-box title key (`*.search`); distinct from route `label` used for tabs / page H1. */
    protected readonly searchBoxHeading = ko.pureComputed(() => getSearchBoxHeadingKey(this.__routeDef));

    /** Heading badge: current hit count when a search is active; hidden while idle. */
    protected readonly searchBadge = ko.pureComputed((): number | null => {
        if (!this.hasSearchKey() || this.isRequesting()) {
            return null;
        }
        return this.searchtotalresults() ?? 0;
    });

    /** Heading spinner while an active search request is in flight. */
    protected readonly searchBadgeWaiting = ko.pureComputed((): boolean => {
        return this.hasSearchKey() && this.isRequesting();
    });

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
        this.reqSearch.abort();
    }
}
